import { AnimationBuilder, AnimationFactory, animate, keyframes, style } from '@angular/animations';

import { SlideComponent } from '../slide.component';
import { BaseSlideBehavior, SlideDirection } from './base-slide-behavior';

interface OneSlideAnimation {
    exitLeft: AnimationFactory;
    exitRight: AnimationFactory;
    enterRight: AnimationFactory;
    enterLeft: AnimationFactory;
    exitLeftInstant: AnimationFactory;
    enterLeftInstant: AnimationFactory;
}

export class OneSlideBehavior extends BaseSlideBehavior {
    constructor(slides: SlideComponent[], animationBuilder: AnimationBuilder, initialSlideIndex: number) {
        super(slides, animationBuilder, initialSlideIndex);

        if (!slides.length) {
            return;
        }

        const animations = this.getAnimations();
        this.slides.forEach((s) => s.animate(animations.exitLeftInstant));
        this.slides[this.currentSlideIndex].animate(animations.enterLeftInstant);
    }

    slideRight(): void {
        this.slideTo(this.previousSlideIndex(), SlideDirection.Right);
    }

    slideLeft(): void {
        this.slideTo(this.nextSlideIndex(), SlideDirection.Left);
    }

    /** @inheritdoc */
    slideTo(slideIndex: number, forceSlideDirection?: SlideDirection): void {
        if (slideIndex === this.currentSlideIndex) {
            return;
        }

        // eslint-disable-next-line no-param-reassign
        slideIndex = this.limitNumber(slideIndex, 0, this.maxSlideIndex);
        const calculatedSlideDirection = slideIndex > this.currentSlideIndex ? SlideDirection.Left : SlideDirection.Right;
        const direction = forceSlideDirection || calculatedSlideDirection;

        if (direction === SlideDirection.Left) {
            this.slideLeftInternal(slideIndex);
        } else {
            this.slideRightInternal(slideIndex);
        }
    }

    get canSlideLeft(): boolean {
        return this.currentSlideIndex < this.maxSlideIndex;
    }

    get canSlideRight(): boolean {
        return this.currentSlideIndex > 0;
    }

    private slideRightInternal(previousSlideIndex: number): void {
        const animations = this.getAnimations();
        this.slides[this.currentSlideIndex].animate(animations.exitRight);
        this.currentSlideIndex = previousSlideIndex;
        this.slides[this.currentSlideIndex].animate(animations.enterLeft);

        this.emitSlideChanged();
    }

    private slideLeftInternal(nextSlideIndex: number): void {
        const animations = this.getAnimations();
        this.slides[this.currentSlideIndex].animate(animations.exitLeft);
        this.currentSlideIndex = nextSlideIndex;
        this.slides[this.currentSlideIndex].animate(animations.enterRight);

        this.emitSlideChanged();
    }

    private getAnimations(): OneSlideAnimation {
        const enterLeftAnimation = this.animationBuilder.build([
            animate(this.animationDuration, keyframes([style({ transform: 'translateX(-100%)' }), style({ transform: 'translateX(0%)' })])),
        ]);

        const enterLeftInstantAnimation = this.animationBuilder.build([
            animate(1, keyframes([style({ transform: 'translateX(-100%)' }), style({ transform: 'translateX(0%)' })])),
        ]);

        const enterRightAnimation = this.animationBuilder.build([
            animate(this.animationDuration, keyframes([style({ transform: 'translateX(100%)' }), style({ transform: 'translateX(0%)' })])),
        ]);

        const exitRightAnimation = this.animationBuilder.build([
            animate(this.animationDuration, keyframes([style({ transform: 'translateX(0%)' }), style({ transform: 'translateX(100%)' })])),
        ]);

        const exitLeftAnimation = this.animationBuilder.build([
            animate(this.animationDuration, keyframes([style({ transform: 'translateX(0%)' }), style({ transform: 'translateX(-100%)' })])),
        ]);

        const exitLeftInstantAnimation = this.animationBuilder.build([
            animate(1, keyframes([style({ transform: 'translateX(0%)' }), style({ transform: 'translateX(-100%)' })])),
        ]);

        return {
            exitLeft: exitLeftAnimation,
            exitRight: exitRightAnimation,
            enterRight: enterRightAnimation,
            enterLeft: enterLeftAnimation,
            exitLeftInstant: exitLeftInstantAnimation,
            enterLeftInstant: enterLeftInstantAnimation,
        };
    }
}
